/**
 * The library itself.
 *
 * A card leads with the picture, because that is how anyone actually finds a
 * file, and carries underneath it only the facts that change a decision: what it
 * is, how big the frame is, how long it runs, whose it is, and when it stops
 * playing. Everything else belongs in the edit dialog.
 */

import type { ReactElement } from 'react';
import { formatBytes } from '../../lib/format.js';
import type { MediaAsset } from '../../lib/media.js';
import {
  assetKind,
  expiryStatus,
  formatDimensions,
  formatDuration,
  orientationLabel,
  scopeLabel,
  typeLabel,
} from './presentation.js';

export interface MediaGridProps {
  assets: readonly MediaAsset[];
  storeNames: ReadonlyMap<string, string>;
  nowMs: number;
  canEdit: (asset: MediaAsset) => boolean;
  onEdit: (asset: MediaAsset) => void;
  onDelete: (asset: MediaAsset) => void;
}

export function MediaGrid({
  assets,
  storeNames,
  nowMs,
  canEdit,
  onEdit,
  onDelete,
}: MediaGridProps): ReactElement {
  return (
    <ul className="media-grid">
      {assets.map((asset) => (
        <MediaCard
          key={`${asset.storeId ?? 'shared'}:${asset.assetId}`}
          asset={asset}
          storeNames={storeNames}
          nowMs={nowMs}
          editable={canEdit(asset)}
          onEdit={() => onEdit(asset)}
          onDelete={() => onDelete(asset)}
        />
      ))}
    </ul>
  );
}

interface MediaCardProps {
  asset: MediaAsset;
  storeNames: ReadonlyMap<string, string>;
  nowMs: number;
  editable: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function MediaCard({
  asset,
  storeNames,
  nowMs,
  editable,
  onEdit,
  onDelete,
}: MediaCardProps): ReactElement {
  const kind = assetKind(asset.type);
  const expiry = expiryStatus(asset.contentExpiresAt, nowMs);
  const orientation = orientationLabel(asset.width, asset.height);

  return (
    <li className="media-card">
      <div className={expiry.kind === 'expired' ? 'media-card__thumb is-expired' : 'media-card__thumb'}>
        {asset.thumbUrl === null ? (
          // No poster frame: the upload happened in a browser that could not
          // encode WebP, or the clip refused to seek. Say which kind of file it
          // is rather than showing a broken image.
          <span className="media-card__placeholder">{kind === 'video' ? 'Video' : 'Image'}</span>
        ) : (
          <img src={asset.thumbUrl} alt="" loading="lazy" decoding="async" />
        )}
        {kind === 'video' ? (
          <span className="media-card__duration">{formatDuration(asset.durationMs)}</span>
        ) : null}
      </div>

      <div className="media-card__body">
        <h4 className="media-card__title" title={asset.title}>
          {asset.title === '' ? 'Untitled' : asset.title}
        </h4>

        <p className="media-card__meta">
          {typeLabel(asset.type)} · {formatDimensions(asset.width, asset.height)} ·{' '}
          {formatBytes(asset.bytes)}
        </p>

        <div className="media-card__badges">
          <span className="chip chip--neutral">{scopeLabel(asset.storeId, storeNames)}</span>
          {orientation !== null ? <span className="chip chip--neutral">{orientation}</span> : null}
          {expiry.kind !== 'none' ? (
            <span className={`chip chip--${expiry.tone}`} title={expiry.detail}>
              {expiry.label}
            </span>
          ) : null}
          {asset.brands.map((brand) => (
            <span className="chip chip--accent" key={brand}>
              {brand}
            </span>
          ))}
        </div>
      </div>

      {editable ? (
        <div className="media-card__actions">
          <button type="button" className="button button--ghost button--small" onClick={onEdit}>
            Edit
          </button>
          <button type="button" className="button button--ghost button--small" onClick={onDelete}>
            Delete
          </button>
        </div>
      ) : null}
    </li>
  );
}
